import React from 'react';
import { Button, Badge, Dropdown } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown, FaFilter } from 'react-icons/fa';
import { IoCalendar, IoCash, IoPeople, IoBusiness, IoLocation } from 'react-icons/io5';

const SortControls = ({
  currentSortField = null,
  currentSortOrder = 'DESC',
  onSortChange = () => {},
  loading = false,
}) => {
  
  const sortOptions = [
    { 
      field: 'created_at', 
      label: 'Date Created',
      icon: <IoCalendar size={14} />,
      description: 'Sort by creation date'
    },
    { 
      field: 'updated_at', 
      label: 'Last Updated',
      icon: <IoCalendar size={14} />,
      description: 'Sort by last update date'
    },
    { 
      field: 'title', 
      label: 'Job Title',
      icon: <IoBusiness size={14} />,
      description: 'Sort alphabetically by job title'
    },
    { 
      field: 'application_deadline', 
      label: 'Application Deadline',
      icon: <IoCalendar size={14} />,
      description: 'Sort by application deadline'
    },
    { 
      field: 'salary_min', 
      label: 'Minimum Salary',
      icon: <IoCash size={14} />,
      description: 'Sort by minimum salary'
    },
    { 
      field: 'salary_max', 
      label: 'Maximum Salary',
      icon: <IoCash size={14} />,
      description: 'Sort by maximum salary'
    },
    { 
      field: 'current_applicants', 
      label: 'Number of Applicants',
      icon: <IoPeople size={14} />,
      description: 'Sort by number of applicants'
    },
    { 
      field: 'experience_required', 
      label: 'Experience Required',
      icon: <IoBusiness size={14} />,
      description: 'Sort by years of experience required'
    },
    { 
      field: 'location', 
      label: 'Location',
      icon: <IoLocation size={14} />,
      description: 'Sort by location'
    },
    { 
      field: 'available_positions', 
      label: 'Available Positions',
      icon: <IoPeople size={14} />,
      description: 'Sort by number of available positions'
    },
  ];

  const renderSortIcon = (field) => {
    if (currentSortField !== field) return <FaSort className="text-muted" />;
    return currentSortOrder === 'ASC' ? <FaSortUp /> : <FaSortDown />;
  };

  const handleSortClick = (field) => {
    onSortChange(field);
  };

  const getCurrentSortLabel = () => {
    const currentOption = sortOptions.find(opt => opt.field === currentSortField);
    return currentOption ? currentOption.label : 'Date Created';
  };

  // Quick sort presets
  const quickSortPresets = [
    { 
      label: 'Newest First', 
      field: 'created_at', 
      order: 'DESC',
      icon: '🆕'
    },
    { 
      label: 'Highest Salary', 
      field: 'salary_max', 
      order: 'DESC',
      icon: '💰'
    },
    { 
      label: 'Deadline Soonest', 
      field: 'application_deadline', 
      order: 'ASC',
      icon: '⏰'
    },
    { 
      label: 'Most Applicants', 
      field: 'current_applicants', 
      order: 'DESC',
      icon: '👥'
    },
    { 
      label: 'Urgent First', 
      field: 'is_urgent', 
      order: 'DESC',
      icon: '⚠️'
    },
  ];

  return (
    <div className="mb-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
        {/* Quick Sort Presets */}
        <div className="d-flex flex-wrap gap-2">
          <span className="text-muted small align-self-center">Quick sort:</span>
          {quickSortPresets.map((preset) => (
            <Button
              key={preset.label}
              variant={
                currentSortField === preset.field && currentSortOrder === preset.order 
                  ? 'primary' 
                  : 'outline-secondary'
              }
              size="sm"
              onClick={() => {
                onSortChange(preset.field, preset.order);
              }}
              disabled={loading}
              className="d-flex align-items-center gap-1"
            >
              <span>{preset.icon}</span>
              {preset.label}
            </Button>
          ))}
        </div>

        {/* Sort Dropdown */}
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Sort by:</span>
          <Dropdown>
            <Dropdown.Toggle 
              variant="outline-primary" 
              size="sm"
              className="d-flex align-items-center gap-2"
              disabled={loading}
            >
              <FaFilter size={12} />
              {getCurrentSortLabel()}
              {renderSortIcon(currentSortField)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {sortOptions.map((option) => (
                <Dropdown.Item 
                  key={option.field}
                  onClick={() => handleSortClick(option.field)}
                  active={currentSortField === option.field}
                  className="d-flex align-items-center gap-2"
                >
                  <span>{option.icon}</span>
                  <div className="d-flex flex-column">
                    <span>{option.label}</span>
                    <small className="text-muted">{option.description}</small>
                  </div>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Sort Order Toggle */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              const newOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
              onSortChange(currentSortField || 'created_at', newOrder);
            }}
            disabled={loading || !currentSortField}
            className="d-flex align-items-center gap-1"
          >
            {currentSortOrder === 'ASC' ? 'A → Z' : 'Z → A'}
          </Button>
        </div>
      </div>

      {/* Current Sort Info */}
      {currentSortField && (
        <div className="mt-3">
          <Badge bg="light" text="dark" className="px-3 py-2">
            <div className="d-flex align-items-center gap-2">
              <FaFilter size={12} />
              <span>
                Sorted by <strong>{getCurrentSortLabel()}</strong> 
                {' '}({currentSortOrder === 'ASC' ? 'Ascending' : 'Descending'})
              </span>
            </div>
          </Badge>
        </div>
      )}
    </div>
  );
};

export default SortControls;